import { useTranslation } from "react-i18next";
import {
  Box,
  LabelStyled,
  SubHeadingStyled,
  ThemeLoader,
} from "../../../components/styled/Elements";
import { ReferralsWrapper } from "./styled";
import { useEffect, useState } from "react";
import { ReferralsListColumns } from "../../../helpers/adminConstants/TableConstants";
import ListTable from "../../../components/common/tables/ListTable";
import ReferralsApi from "../../../services/api/referralsApi";
import { toast } from "react-toastify";
import DeleteModal from "../../../components/admin/Modals/DeleteModal/DeleteModal";
import ReferralOpenModal from "../../../components/admin/Modals/ReferralModals/ReferralOpenModal";
import { Col, Row } from "react-bootstrap";
import FormRadio from "../../../components/common/radioButtons/FormRadio";
import { DropdownWithSearch } from "../../../components/common/dropdowns/DropdownInputs";
import { useSelector } from "react-redux";
import { SearchIcon } from "../../../components/styled/AllImages";
import FloatingInput from "../../../components/common/inputFields/FloatingInput";
import { referralsTileArray } from "../../../helpers/adminConstants/AdminConstants";
import CustomTile from "../../../components/common/statusTiles/CustomTile";
import { useNavigate } from "react-router-dom";

const Referrals = () => {
  const navigate = useNavigate();
  const { t, i18n: { language } } = useTranslation();

  const { hqBranchesList, branchesDeps } = useSelector((state) => state.helper);

  const [total, setTotal] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [activePage, setActivePage] = useState(1);
  const [referralId, setReferralId] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [referralNoteText, setReferralNoteText] = useState("");
  const [isReferralOpenModal, setIsReferralOpenModal] = useState(false);
  const [referralStats, setReferralStats] = useState(referralsTileArray);

  const [filterParams, setFilterParams] = useState({
    type: "",
    search_branch: null,
    search_department: null,
    search_value: null,
  });

  const formattedHqBranchesList =
    hqBranchesList &&
    hqBranchesList.map((item) => ({
      id: item?.id,
      name: language === "en" ? item.name_en : item.name_ar,
    }));

  const getReferralsList = async () => {
    try {
      setLoading(true);
      const { data, code } = await ReferralsApi.referralsList(activePage, filterParams);

      if (code === 200) {
        // Stats update logic
        const updatedStats = [...referralStats];
        updatedStats[0].count = data?.stats?.total || 0;
        updatedStats[1].count = data?.stats?.pending || 0;
        setReferralStats(updatedStats);

        console.log('data.list: ', data.list);

        setReferrals(data?.list ?? []);
        setPerPage(data?.pagination?.per_page);
        setTotal(data?.pagination?.total);
      } else {
        setReferrals([]);
        // Reset stats on error/no data
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onDeleteClick = (row) => {
    setReferralId(row.id);
    setShowDelete(true);
  };

  const handleDeleteReferral = async () => {
    try {
      const response = await ReferralsApi.deleteReferral(referralId);
      if (response.code === 200) {
        toast.success(response.message);
        setShowDelete(false);
        getReferralsList();
      }
    } catch (error) {
      toast.error(t("something_went_wrong"));
    }
  };

  const onChange = (e) => {
    let { name, value } = e.target;
    
    if (name === "search_department") value = parseInt(value);
    else if (name === "search_branch") value = parseInt(value);
    else if (name === "type")
      value = value;

    setFilterParams((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleOpen = (row) => {
    setIsReferralOpenModal(true);
    setReferralNoteText(row.note ?? "");
  };

  useEffect(() => {
    if (branchesDeps && branchesDeps.length > 0) {
      const deptList = branchesDeps?.filter(
        (item) => item?.location_id === filterParams?.search_branch
      );
      setDepartmentsList(deptList);
    }
  }, [branchesDeps, filterParams]);

  useEffect(() => {
    getReferralsList();
  }, [activePage, language,  filterParams]);

  return (
    <ReferralsWrapper>
      <Box className="content-head d-flex justify-content-between align-items-center">
        <SubHeadingStyled size="18px" weight="700" color="var(--text)">
          {t("Referrals")}
        </SubHeadingStyled>
      </Box>

      <Row className="mx-2 mt-2">
        <Col className="my-2">
          <Box className="d-flex type-select-box">
            <LabelStyled size="14px" className="mx-2">
              {t("type")}
            </LabelStyled>
            <Box className="d-flex filters-list">
              {/* All Referrals */}
              <FormRadio
                name="type"
                subLabel={t("all")}
                value={""}
                checked={!filterParams.type}
                onChange={onChange}
              />

              {/* Received Referrals (Type 1) */}
              <FormRadio
                name="type"
                subLabel={t("received")}
                value={"1"}
                checked={filterParams.type === "1"}
                onChange={onChange}
              />

              {/* Sent Referrals (Type 2) */}
              <FormRadio
                name="type"
                subLabel={t("sent")}
                value={"2"}
                checked={filterParams.type === "2"}
                onChange={onChange}
                className={"text-nowrap"}
              />
            </Box>
          </Box>
        </Col>
        <Col className="my-2">
          <DropdownWithSearch
            name={"search_branch"}
            optionsList={formattedHqBranchesList}
            selectedoption={filterParams?.search_branch}
            placeholder={t("search_by_hqbranch")}
            onChange={onChange}
          />
        </Col>
        <Col className="my-2">
          <DropdownWithSearch
            name={"search_department"}
            optionsList={departmentsList}
            selectedoption={filterParams?.search_department}
            placeholder={t("search_by_department")}
            onChange={onChange}
          />
        </Col>
        <Col className="my-2">
          <FloatingInput
            name="search_value"
            type="input"
            fieldType="text"
            label={t("search")}
            placeholder={t("search")}
            bg="var(--white)"
            icon={SearchIcon}
            onChange={onChange}
          />
        </Col>
      </Row>

      <Row className="mx-2 d-flex justify-content-center">
        {referralStats.map((tile, key) => (
          <Col lg={3} md={6} sm={12} className="my-2" key={key}>
            <CustomTile {...tile} />
          </Col>
        ))}
      </Row>

      <Box className="mt-4">
        {loading ? (
          <ThemeLoader />
        ) : (
          <ListTable
            options={["open", "view", "delete"]}
            rows={referrals}
            typeId={1}
            columns={ReferralsListColumns}
            onDelete={onDeleteClick}
            onOpen={handleOpen}
            onViewDetails={(row) => navigate(`/referrals/view/${row.id}`)}
            activePage={activePage}
            setActivePage={setActivePage}
            totalCount={total}
            perPage={perPage}
          />
        )}
      </Box>

      <DeleteModal
        show={showDelete}
        onHide={() => setShowDelete(false)}
        title={t("referral")}
        onDelete={handleDeleteReferral}
      />

      <ReferralOpenModal
        show={isReferralOpenModal}
        onHide={() => setIsReferralOpenModal(false)}
        note={referralNoteText}
      />
    </ReferralsWrapper>
  );
};

export default Referrals;
